import { MatrixSizes } from "../../constants/PotentialMatrixConstants";
import styled, { css, DefaultTheme, keyframes } from "styled-components";
import { theme } from "../../constants/StyledComponents";

const shrinkAway = keyframes`
  from {
    opacity: 1;
    transform: scale(1) translate(0, 0);
  }
  to {
    opacity: 0;
    transform: scale(0.6) translate(30%, 30%);
  }
`;

export const MATRIX_ROW_LABEL_WIDTH = {
  small: "136px",
  big: "clamp(132px, 10%, 180px)",
};

export const StyledMatrix = styled.div<{
  $size: MatrixSizes;
  $isAnimatingOpen?: boolean;
}>`
  display: flex;
  flex-direction: column;
  padding: 10px;
  flex-grow: 1;
  position: relative;
  overflow-y: auto;
  animation: ${(props) =>
    props.$isAnimatingOpen
      ? css`
          ${shrinkAway} 0.5s ease-out forwards
        `
      : "none"};

  .row-title {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    width: max-content;
    min-width: 120px;
    align-items: flex-end;
    white-space: nowrap;
    padding-right: 16px;
  }

  .matrix-content {
    display: flex;
    flex-direction: row;
    flex: 1;
    min-width: 0;
  }

  .container-internal {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .loader-matrix-container,
  .loader-matrix-content {
    width: 100%;
    height: 100%;
  }

  .row {
    display: flex;
    flex-direction: row;
    box-sizing: border-box;
    gap: ${(props) => (props.$size === MatrixSizes.BIG ? "8px" : "4px")};
  }

  .matrix-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: ${(props) => (props.$size === MatrixSizes.BIG ? "8px" : "4px")};
    height: 100%;
  }

  .cell {
    display: flex;
    flex-direction: column;
    margin: 5px;
    border-radius: 12px;
    padding: 10px;
    gap: 10px;
    width: 100px;
    height: 100px;
    justify-content: center;
  }

  .cell span .avatar-container {
    margin-right: 10px;
  }
`;

const headerVariantStyles = (theme: DefaultTheme, $size: MatrixSizes) =>
  ({
    small: css`
      gap: 2px;
    `,
    big: css`
      gap: 8px;
    `,
  })[$size];

export const StyledQuadrantAxisLabels = styled.div<{
  $size: MatrixSizes;
  $numCol: number;
}>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$numCol
      ? `${props.$size === MatrixSizes.BIG ? MATRIX_ROW_LABEL_WIDTH.big : MATRIX_ROW_LABEL_WIDTH.small} repeat(${props.$numCol}, 1fr)`
      : ""};
  justify-items: center;
  align-items: center;
  ${({ theme, $size }) => headerVariantStyles(theme, $size)};
  padding-top: 12px;

  & > .axis-cell {
    grid-column: span 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  & > .axis-cell:first-child {
    grid-column: 2;
  }
`;

export const variantStyles = (
  theme: DefaultTheme,
  size: MatrixSizes,
  showDownloadPreview: boolean,
) =>
  ({
    small: css`
      width: 50px;
      height: 50px;
      border-radius: 4px;
      align-items: center;
    `,
    big: css`
      width: 100%;
      border-radius: 8px;
      min-height: ${showDownloadPreview ? "510px" : "124px"};
      min-width: 0;
    `,
  })[size];

export const StyledCell = styled.div<{
  $bgColor: string;
  $size: MatrixSizes;
  $showDownloadPreview: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
  ${({ theme, $size, $showDownloadPreview }) =>
    variantStyles(theme, $size, $showDownloadPreview)};
  background-color: ${(props) => props.$bgColor};

  & span .avatar-container {
    margin-right: 10px;
  }
`;

export const StyledCellContainer = styled.div<{
  $clickable?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  height: 100%;

  & .cell-main-box {
    border-radius: ${theme.variables.border_radius_5};
    cursor: ${(props) => (props.$clickable ? "pointer" : "inherit")};
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    height: 100%;

    ${(props) =>
      props.$clickable &&
      css`
        &:hover {
          background-color: rgba(255, 255, 255, 0.7);
        }
      `}
  }

  & .cell-main-box-small {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
  }

  & .cell-main-box.active {
    background-color: rgba(255, 255, 255, 0.7);
  }

  & .count-container {
    border-radius: 100%;
    background-color: ${theme.colors.purple_2};
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const StyledCellItem = styled.div`
  background-color: white;
  border-radius: 100px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  gap: 12px;

  &:hover {
    opacity: 70%;
  }

  & .icon-container {
    background: ${theme.colors.purple_2};
    border-radius: 100%;
    padding: 8px;
  }
`;

export const StyledCircle = styled.div<{
  $size: number;
  $isVisible: boolean;
}>`
  width: ${(props) => `${props.$size}px`};
  height: ${(props) => `${props.$size}px`};
  border-radius: 100%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 0px 8px 0px rgba(74, 74, 79, 0.2);
  position: absolute;
  visibility: ${(props) => (props.$isVisible ? "visible" : "hidden")};
`;

export const StyledCircleGroupContainer = styled.div<{
  $size: number;
}>`
  position: relative;
  width: ${(props) => `${props.$size}px`};
  height: ${(props) => `${props.$size}px`};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const StyledCellContentGloabal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const StyledCellContentInternalGrades = styled.div`
  padding: 4px;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: center;
  gap: 4px;

  & .internal-grade-chip.invisible {
    visibility: hidden;
  }
`;

export const StyledCellContentCollaborators = styled.div`
  display: flex;
  padding: 2px;
  height: 100%;

  & .cell-content-collaborators-header {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex-grow: 1;
  }

  & .cell-content-collaborators-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    gap: 2px;
    width: 100%;
    min-width: 0;

    @media (max-width: ${theme.breakpoints.XL}px) {
      grid-template-columns: repeat(1, 1fr);
    }
  }

  & .number-chip-box {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 2px 0;
  }

  & .number-chip-container.invisible {
    visibility: hidden;
  }

  & .collaborator-chip-slot {
    min-width: 0;
    padding: 2px 2px 0px 2px;
  }

  & .collaborator-chip-slot.invisible {
    visibility: hidden;
  }
`;

export const StyledCellContentDownload = styled.div`
  display: flex;
  padding: 2px;
  width: 100%;

  & .cell-content-collaborators-header {
    display: flex;
    flex-grow: 1;
  }

  & .cell-content-collaborators-container {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    align-items: center;
    gap: 2px;
    width: 100%;
  }
`;

export const StyledMatrixButton = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 8px;
  min-height: 17px;

  & .details-icon-wrapper {
    position: absolute;
    right: 1px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 17px;
    border-radius: 20px;
    background: white;
    border: 1px solid white;
  }
`;

export const StyledIconContainer = styled.div<{
  $isDark: boolean;
  $isVisible: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  width: 18px;
  border-radius: 3px;
  background-color: ${(props) =>
    props.$isDark ? theme.colors.purple : theme.colors.purple_2};
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  visibility: ${(props) => (props.$isVisible ? "visible" : "hidden")};
  transition: opacity 0.3s ease;
`;

export const StyledBottomBar = styled.div<{ $isActive: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 22px;
  padding: 2px;
  box-sizing: border-box;
  background-color: ${(props) =>
    props.$isActive ? theme.colors.white : "rgba(255, 255, 255, 0.7)"};
  border-radius: ${theme.variables.border_radius_5};
  transition: all 0.3s ease;

  & .draggable-collaborator-chip {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    & .chip-container {
      display: flex;
      position: absolute;
      justify-content: center;
      width: 100%;
      z-index: 10;
      overflow: hidden;
      cursor: default;

      & > div {
        max-width: 200px;
        min-width: 0px;
        width: 60%;
      }
    }

    & .placeholder-chip {
      z-index: 5 !important;
      pointer-events: none;
    }

    & .dnd-placeholder {
      width: 0;
      height: 0;
      overflow: hidden;
    }
  }

  & .release-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;
  }
`;
